import React from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { FileText } from 'lucide-react';

const GRUNNTONER = [
  'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B'
];

interface SongTextCardProps {
  grunntone: string;
  toneartType: string;
  tekstOgAkkorder: string;
  onGrunntoneChange: (value: string) => void;
  onToneartTypeChange: (value: string) => void;
  onTekstOgAkkorderChange: (value: string) => void;
}

export const SongTextCard: React.FC<SongTextCardProps> = ({
  grunntone,
  toneartType,
  tekstOgAkkorder,
  onGrunntoneChange,
  onToneartTypeChange,
  onTekstOgAkkorderChange,
}) => {
  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-400" />
      <div className="p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FileText className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Tekst og akkorder</h2>
            <p className="text-sm text-muted-foreground">Original versjon av sangen</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Original toneart */}
          <div className="space-y-2">
            <Label>Original toneart</Label>
            <div className="flex gap-2 max-w-xs">
              <Select value={grunntone} onValueChange={onGrunntoneChange}>
                <SelectTrigger className="w-20">
                  <SelectValue placeholder="Velg" />
                </SelectTrigger>
                <SelectContent>
                  {GRUNNTONER.map((tone) => (
                    <SelectItem key={tone} value={tone}>
                      {tone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={toneartType} onValueChange={onToneartTypeChange}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dur">Dur</SelectItem>
                  <SelectItem value="moll">Moll</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tekst med akkorder */}
          <div className="space-y-2">
            <Label htmlFor="lyrics">Sangtekst med akkorder</Label>
            <Textarea
              id="lyrics"
              value={tekstOgAkkorder}
              onChange={(e) => onTekstOgAkkorderChange(e.target.value)}
              className="min-h-[400px] font-mono text-sm leading-relaxed resize-y bg-slate-50/50 focus:bg-white placeholder:font-sans placeholder:italic"
              placeholder="Ingen tekst lagt inn ennå. Skriv eller lim inn sangteksten her – se formateringstips under."
            />
            
            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2">Tips for formatering (to formater støttes):</p>
              <div className="space-y-3 text-muted-foreground">
                <div>
                  <p className="font-medium text-foreground mb-1">Format 1: Akkorder på egen linje</p>
                  <pre className="text-xs bg-white/50 p-2 rounded">
{`Verse\nC    G/B    F/A    C\nHoly ho - ly  are You Lord`}
                  </pre>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Format 2: Inline [akkord]-notasjon</p>
                  <pre className="text-xs bg-white/50 p-2 rounded">
{`Verse 1\nI have this [C#m]hope\nAs an anchor for [E]my soul`}
                  </pre>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">Tall er også lov, på egen linje eller i hakeparentes</p>
                  <pre className="text-xs bg-white/50 p-2 rounded">
{`1 5 6m 4\nHoly ho - ly  are You Lord\nI have this [1]hope`}
                  </pre>
                </div>
                <p className="text-xs">💡 Seksjoner: Intro, Verse, Chorus, Bridge, Outro</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

